/**
 * Preload images in the background for better UX
 */

export interface PreloadResult {
  loaded: string[];
  failed: string[];
}

/**
 * Preload a single image
 */
const normalizeUrl = (url: string | null | undefined): string => {
  if (!url || typeof url !== 'string') return '';
  return url.trim();
};

const preloadImage = (url: string): Promise<{ url: string; ok: boolean }> => {
  return new Promise((resolve) => {
    const normalized = normalizeUrl(url);
    if (!normalized) {
      resolve({ url, ok: false });
      return;
    }

    const img = new Image();
    const done = (ok: boolean) => {
      img.onload = null;
      img.onerror = null;
      img.onabort = null;
      resolve({ url: normalized, ok });
    };

    img.onload = () => done(true);
    img.onerror = () => done(false);
    img.onabort = () => done(false);
    img.src = normalized;
  });
};

/**
 * Preload multiple images with progress tracking
 */
export const preloadImages = async (
  imageUrls: string[],
  onProgress?: (loaded: number, total: number) => void
): Promise<PreloadResult> => {
  const result: PreloadResult = {
    loaded: [],
    failed: [],
  };

  const urls = Array.isArray(imageUrls)
    ? Array.from(new Set(imageUrls.map(normalizeUrl))).filter(Boolean)
    : [];
  const total = urls.length;

  if (!total) {
    return result;
  }

  await Promise.all(
    urls.map(async (url) => {
      const { ok } = await preloadImage(url);
      if (ok) {
        result.loaded.push(url);
      } else {
        result.failed.push(url);
      }
      if (onProgress) {
        onProgress(result.loaded.length + result.failed.length, total);
      }
    }),
  );

  return result;
};

/**
 * Preload images in chunks to avoid overwhelming the browser
 */
export const preloadImagesInChunks = async (
  imageUrls: string[],
  chunkSize: number = 5,
  onProgress?: (loaded: number, total: number) => void
): Promise<PreloadResult> => {
  const result: PreloadResult = {
    loaded: [],
    failed: []
  };

  for (let i = 0; i < imageUrls.length; i += chunkSize) {
    const chunk = imageUrls.slice(i, i + chunkSize);
    const chunkResult = await preloadImages(chunk, onProgress);
    
    result.loaded.push(...chunkResult.loaded);
    result.failed.push(...chunkResult.failed);
  }

  return result;
};

/**
 * Preload images with timeout
 */
export const preloadImagesWithTimeout = async (
  imageUrls: string[],
  timeout: number = 10000, // 10 seconds default
  onProgress?: (loaded: number, total: number) => void
): Promise<PreloadResult> => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let didTimeout = false;

  const timeoutPromise = new Promise<PreloadResult>((resolve) => {
    timeoutId = setTimeout(() => {
      didTimeout = true;
      resolve({ loaded: [], failed: imageUrls });
    }, timeout);
  });

  const preloadPromise = preloadImages(imageUrls, onProgress);

  const result = await Promise.race([preloadPromise, timeoutPromise]);

  if (timeoutId) {
    clearTimeout(timeoutId);
  }
  if (didTimeout) {
    console.warn('Image preloading timed out, continuing with partial results');
  }
  return result;
};
